// Publishing a document by link: the item "Publish by link" in the document's
// "…", the mark beside the favorite star while it is published, and the
// dialog both open. Put into the document screen through the shell's header
// actions (`publishPart`: `headerActions.page`). The server is cloud's: cloud's
// web puts this part in, and the offline edition does while personal data is
// read from the cloud (its core sends the requests there); otherwise it has
// none.
//
// - Not published: the page as it would be published (the server draws it), how
//   many references will show only their kind (links taken off, for html and
//   slide), the datasets that go out with it (datasets.tsx), and "Publish"
//   (sending the ones shown).
// - Published: the URL with copy and open, when it was published, the
//   datasets out with it and those read but not published, "Stop publishing".
//   The mark gets a dot while the body reads a dataset that is not published
//   (the screen reads the body; nothing is asked of the server for it).
//   There is no event for "the copy is up", so right after publishing (builtAt
//   still null) the dialog only says it takes a few seconds; it does not poll.
// - On a phone the dialog fills the screen, as a screen pushed from the right
//   (it slides in). In a phone app that draws the bar at the top, its way out
//   is the app's own back there (a header over the screen, `useShellHeader`
//   with `over` and `onBack`; the app's edge swipe does the same): the dialog
//   slides back out to the right. A dataset's back and open are there too.
//
// The preview frame loads the server's page for it (its signed address), not
// a srcdoc: a srcdoc takes this screen's policy, and the desktop app's lets
// no image, diagram or UI in. The page brings its own. The frame is
// `sandbox="allow-scripts"` only: together with allow-same-origin its scripts
// would run with the app's origin. Scripts are needed (diagrams, UIs, an html
// document's own).
import { useQuery } from "@tanstack/react-query";
import type { PageDTO } from "@trameria/core/documents-shared";
import type { PublicationDTO } from "@trameria/core/publishing-shared";
import { m } from "@trameria/i18n";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../app/context";
import {
  defineScreenPart,
  type HeaderAction,
  type HeaderActionProps,
  type HeaderActionTarget,
} from "../app/screen-part";
import { apiErrorMessage } from "../data/errors";
import {
  type DatasetInPlace,
  DatasetView,
  reducedMotion,
  useSlideIn,
} from "../datasets/viewer";
import { useFormat } from "../me/profile";
import { useShellHeader } from "../shell/outside/outside";
import { useHostDrawsBar, usePhone } from "../shell/phone";
import { ErrorLine, Skeleton } from "../ui/bits";
import { Button, buttonClass, IconButton } from "../ui/button";
import { confirmAction } from "../ui/confirm";
import { Icon } from "../ui/icon";
import { Modal } from "../ui/modal";
import { toast } from "../ui/toast";
import { Tooltip } from "../ui/tooltip";
import { DatasetsToPublish, PreviewData, PublishedDatasets } from "./datasets";
import {
  previewQuery,
  publicationQuery,
  usePublish,
  useUnpublish,
} from "./queries";

const NOTE = "text-[12.5px] leading-[1.7] text-muted";

const PUBLISHABLE = new Set<PageDTO["kind"]>(["markdown", "html", "slide"]);

/** What the document screen hands to its header actions (`{ page, writable, datasetIds }`). */
const documentOf = (ext: HeaderActionTarget["ext"]) =>
  ext as
    | { page: PageDTO; writable: boolean; datasetIds?: readonly string[] }
    | undefined;

const publishAction: HeaderAction = {
  id: "publish",
  menuItem: ({ ext }) => {
    const doc = documentOf(ext);
    // One's own documents and the workspace's one may write (the server
    // refuses a workspace that does not allow publishing, with its reason).
    return doc?.writable &&
      doc.page.archivedAt === null &&
      PUBLISHABLE.has(doc.page.kind)
      ? { label: m.publishing_menu() }
      : null;
  },
  Component: PublishHeader,
  useAction: usePublishedMark,
};

/**
 * The globe while published, as data for a bar drawn outside (the app's):
 * pressing it opens the dialog. The web draws its own (`PersonalPublishHeader`).
 */
function usePublishedMark({ targetId, ext }: HeaderActionTarget) {
  const api = useApi();
  const doc = documentOf(ext);
  const state = useQuery({
    ...publicationQuery(api, targetId),
    enabled: doc?.page !== undefined,
  });
  const publication = state.data?.publication;
  if (!doc?.page || !publication) return null;
  const pending = (doc.datasetIds ?? []).some(
    (id) => !publication.datasetIds.includes(id),
  );
  return {
    label: pending
      ? m.publishing_published_pending()
      : m.publishing_published(),
    icon: "globe",
  };
}

/** Publishing by link, in the header of a document (the shell's `headerActions`). */
export const publishPart = defineScreenPart({
  id: "publish",
  headerActions: { page: [publishAction] },
});

function PublishHeader({ ext, ...rest }: HeaderActionProps) {
  const doc = documentOf(ext);
  if (!doc?.page) return null;
  return (
    <PersonalPublishHeader
      page={doc.page}
      datasetIds={doc.datasetIds ?? []}
      {...rest}
    />
  );
}

function PersonalPublishHeader({
  page,
  datasetIds,
  opened,
  open,
  close,
}: Pick<HeaderActionProps, "opened" | "open" | "close"> & {
  page: PageDTO;
  datasetIds: readonly string[];
}) {
  const api = useApi();
  const phone = usePhone();
  const hostBar = useHostDrawsBar() && phone;
  const state = useQuery(publicationQuery(api, page.id));
  const publication = state.data?.publication;
  // Read by the body but not out with the page.
  const pending =
    publication !== undefined &&
    publication !== null &&
    datasetIds.some((id) => !publication.datasetIds.includes(id));
  const label = pending
    ? m.publishing_published_pending()
    : m.publishing_published();
  return (
    <>
      {publication && (
        <Tooltip text={label}>
          <IconButton label={label} onClick={open}>
            <span className="relative inline-flex">
              <Icon kind="globe" size={15} />
              {pending && (
                <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-warn" />
              )}
            </span>
          </IconButton>
        </Tooltip>
      )}
      <Modal
        open={opened}
        onClose={close}
        label={m.publishing_title()}
        // A phone (the web's and the app's) gives it the whole screen.
        size={phone ? "full" : "wide"}
        hostBar={hostBar}
        // `relative`: a dataset slides in over its own view. With the
        // datasets' list under the preview it can outgrow the window: its own
        // view scrolls inside instead of running off the bottom.
        className={
          phone
            ? "relative"
            : "relative flex max-h-[84vh] flex-col overflow-hidden"
        }
      >
        {opened && <PublishBody page={page} onClose={close} />}
      </Modal>
    </>
  );
}

function PublishBody({ page, onClose }: { page: PageDTO; onClose(): void }) {
  const api = useApi();
  const phone = usePhone();
  // The app's bar at the top has the way back (see the top of the file).
  const hostBar = useHostDrawsBar() && phone;
  const root = useRef<HTMLDivElement>(null);
  const panel = () => root.current?.closest<HTMLElement>("[data-modal-panel]");
  // On a phone the dialog is a screen pushed from the right.
  useSlideIn(() => (phone ? panel() : null));
  // Back in the app: out to the right, then closed.
  const [leaving, setLeaving] = useState(false);
  const leave = () => {
    if (reducedMotion() || !panel()?.animate) onClose();
    else setLeaving(true);
  };
  // frontend-development: not a fetch; plays the slide out, then closes.
  useLayoutEffect(() => {
    const el = leaving
      ? root.current?.closest<HTMLElement>("[data-modal-panel]")
      : null;
    if (!el) return;
    for (const a of el.getAnimations()) a.cancel();
    const out = el.animate(
      [{ transform: "none" }, { transform: "translateX(100%)" }],
      {
        duration: 200,
        easing: "cubic-bezier(0.4, 0, 0.6, 1)",
        fill: "forwards",
      },
    );
    out.onfinish = () => onClose();
    return () => {
      out.onfinish = null;
    };
  }, [leaving, onClose]);
  useShellHeader(
    hostBar
      ? {
          title: m.publishing_title(),
          over: true,
          // Slid off by the app's edge swipe: closed at once.
          onBack: (how) => (how?.slid ? onClose() : leave()),
          layer: panel,
          actions: [],
        }
      : null,
  );
  const state = useQuery(publicationQuery(api, page.id));
  const publication = state.data?.publication ?? null;
  // A dataset of the list shown over the dialog's own view (null: none).
  const [viewing, setViewing] = useState<DatasetInPlace | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const view = (d: DatasetInPlace) => {
    opener.current = document.activeElement as HTMLElement | null;
    setViewing(d);
  };
  // frontend-development: not a fetch; moves the focus into the dataset's
  // view and back to the name it was opened from.
  useLayoutEffect(() => {
    if (viewing)
      root.current?.parentElement
        ?.querySelector<HTMLElement>("[data-dataset-back]")
        ?.focus({ preventScroll: true });
    else opener.current?.focus({ preventScroll: true });
  }, [viewing]);
  return (
    <>
      {/* Stays as it was under a dataset (its scroll, its preview). */}
      <div
        ref={root}
        inert={viewing !== null || undefined}
        className={`panel-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          hostBar
            ? "px-4 pt-[var(--top-dock-clearance)] pb-[calc(24px+env(safe-area-inset-bottom))]"
            : phone
              ? "px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-[calc(24px+env(safe-area-inset-bottom))]"
              : "-m-5 p-5"
        }`}
      >
        <div className="flex flex-col gap-3">
          {hostBar ? (
            <h2 className="mt-1 text-[17px] font-normal text-fg">
              {m.publishing_title()}
            </h2>
          ) : (
            <div className="flex items-start gap-2">
              <h2 className="min-w-0 flex-1 text-[15px] font-normal text-fg">
                {m.publishing_title()}
              </h2>
              <IconButton label={m.publishing_close()} onClick={onClose}>
                <Icon kind="close" size={15} />
              </IconButton>
            </div>
          )}
          {state.isPending ? (
            <Skeleton lines={[70, 90, 50]} />
          ) : state.isError ? (
            <p className={NOTE}>
              {apiErrorMessage(state.error, m.publishing_failed())}
            </p>
          ) : publication ? (
            <Published
              page={page}
              publication={publication}
              onStopped={onClose}
              onView={view}
            />
          ) : (
            <NotPublished page={page} onView={view} />
          )}
        </div>
      </div>
      {viewing && (
        <DatasetView
          dataset={viewing}
          onClose={onClose}
          back={{
            label: m.publishing_title(),
            onBack: () => setViewing(null),
          }}
        />
      )}
    </>
  );
}

function NotPublished({
  page,
  onView,
}: {
  page: PageDTO;
  onView(dataset: DatasetInPlace): void;
}) {
  const api = useApi();
  const preview = useQuery(previewQuery(api, page.id));
  const publish = usePublish(page.id);
  const frame = useRef<HTMLIFrameElement>(null);
  const plain = preview.data?.plainRefCount ?? 0;
  const withDatasets =
    (preview.data?.datasets.length ?? 0) +
      (preview.data?.hiddenDatasetCount ?? 0) >
    0;
  const going = useMemo(
    () =>
      (preview.data?.datasets ?? [])
        .filter((d) => d.state === "publish")
        .map((d) => d.id),
    [preview.data],
  );
  return (
    <>
      <p className={NOTE}>{m.publishing_intro()}</p>
      {page.kind === "html" && (
        <p className={NOTE}>{m.publishing_html_scripts()}</p>
      )}
      {/* Shorter with the datasets' list, so "Publish" stays in the window. */}
      <div
        className={`${withDatasets ? "h-[min(40vh,420px)]" : "h-[min(56vh,520px)]"} overflow-hidden rounded-[9px] border border-border bg-surface`}
      >
        {preview.data ? (
          <iframe
            ref={frame}
            // A frame needs a title to be named; aria-label says the same.
            title={m.publishing_preview()}
            aria-label={m.publishing_preview()}
            // See the top of the file: never with allow-same-origin.
            sandbox="allow-scripts"
            src={preview.data.url}
            className="block h-full w-full border-0"
          />
        ) : preview.isError ? (
          <p className={`p-4 ${NOTE}`}>
            {apiErrorMessage(preview.error, m.publishing_preview_failed())}
          </p>
        ) : (
          <div className="p-4">
            <Skeleton lines={[50, 90, 80, 60]} />
          </div>
        )}
      </div>
      {preview.data && (
        <PreviewData
          frame={frame}
          scope={page.ownerUserId === null ? "space" : "personal"}
          going={going}
        />
      )}
      {plain > 0 && (
        <p className={NOTE}>
          {page.kind === "markdown"
            ? m.publishing_unpublished_refs({ count: plain })
            : m.publishing_unpublished_links({ count: plain })}
        </p>
      )}
      {preview.data && (
        <DatasetsToPublish
          datasets={preview.data.datasets}
          hidden={preview.data.hiddenDatasetCount}
          onView={onView}
        />
      )}
      {publish.isError && (
        <ErrorLine>
          {apiErrorMessage(publish.error, m.publishing_failed())}
        </ErrorLine>
      )}
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!preview.isSuccess || publish.isPending}
          onClick={() => publish.mutate(going)}
        >
          {m.publishing_action()}
        </Button>
      </div>
    </>
  );
}

function Published({
  page,
  publication,
  onStopped,
  onView,
}: {
  page: PageDTO;
  publication: PublicationDTO;
  onStopped(): void;
  onView(dataset: DatasetInPlace): void;
}) {
  const format = useFormat();
  const unpublish = useUnpublish(page.id);
  const url = publication.url;
  const copy = () =>
    navigator.clipboard.writeText(url).then(
      () => toast(m.publishing_copied()),
      () => toast(m.publishing_copy_failed()),
    );
  const stop = async () => {
    const ok = await confirmAction({
      title: m.publishing_stop_title({ title: page.title }),
      body: m.publishing_stop_body(),
      action: m.publishing_stop(),
      danger: true,
    });
    if (ok) unpublish.mutate(undefined, { onSuccess: onStopped });
  };
  return (
    <>
      <div className="flex items-center gap-2">
        <input
          type="text"
          readOnly
          aria-label={m.publishing_url()}
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className="h-8 min-w-0 flex-1 rounded-[7px] border border-field bg-surface px-2.5 text-[12.5px] text-body outline-none"
        />
        <Button onClick={() => void copy()}>{m.publishing_copy()}</Button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass("ghost")}
        >
          {m.publishing_open()}
        </a>
      </div>
      <p className={NOTE}>
        {publication.builtAt === null
          ? m.publishing_building()
          : m.publishing_published_at({
              date: format.dateTime(publication.createdAt),
            })}
      </p>
      {publication.failing && (
        <p className="text-[12.5px] leading-[1.7] text-warn">
          {m.publishing_failing()}
        </p>
      )}
      <PublishedDatasets pageId={page.id} onView={onView} />
      {unpublish.isError && (
        <ErrorLine>
          {apiErrorMessage(unpublish.error, m.publishing_stop_failed())}
        </ErrorLine>
      )}
      <div className="flex justify-end">
        <Button disabled={unpublish.isPending} onClick={() => void stop()}>
          {m.publishing_stop()}
        </Button>
      </div>
    </>
  );
}
